import { DESIGN_HUDDLE_CLIENT_ID, DESIGN_HUDDLE_DOMAIN } from './constants'
import {
  DHEditorInstance,
  DHEditorLib,
  DHElementsData,
  DHLibConfig,
  DHProject,
  DHProjectGetRenderResult,
  DHTco,
  DHUserMode
} from '../../../types/design-huddle.types'

/**
 * Builds a config object to be passed to the Design Huddle library.
 * When creating a project/opening the editor, configure the SDK as a guest by passing `{ mode: 'guest' }`.
 * For the template gallery view, configure the SDK as a visitor by passing `{ mode: 'visitor' }`.
 *
 * @example
 * const config = buildDHConfigObject({ mode: 'guest' })
 * window.DSHDEditorLib?.configure(config)
 */
export const buildDHConfigObject = ({
  mode,
  tco
}: {
  mode: DHUserMode
  tco?: DHTco
}): DHLibConfig => {
  return {
    domain: DESIGN_HUDDLE_DOMAIN,
    client_id: DESIGN_HUDDLE_CLIENT_ID,
    [mode]: true,
    ...(tco && { template_customization_object: { classes: tco } })
  }
}

/**
 * Get a thumbnail URL for a Design Huddle template, customized with the provided {@link DHTco}.
 *
 * Promisifies the callback-based {@link DHEditorLib.getVariableTemplatePreviewURL} method.
 */
export const getTemplatePreviewURL = (dhTemplateId: string, tco?: DHTco): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!window?.DSHDEditorLib) {
      reject(new Error('Design Huddle editor library not found'))
    }

    window?.DSHDEditorLib?.getVariableTemplatePreviewURL(
      {
        template_id: dhTemplateId,
        ...(tco && { customizations: { classes: tco } })
      },
      (error, image_url) => {
        if (error) {
          console.error(error, image_url)
          reject(error)
        } else if (!image_url) {
          reject(new Error('Failed to get template preview URL for the supplied template'))
        } else {
          resolve(image_url)
        }
      }
    )
  })
}

/**
 * Get a list of {@link DHProject}s.
 *
 * Promisifies the callback-based {@link DHEditorLib.getProjects} method.
 */
export const getProjects = (): Promise<DHProject[]> => {
  return new Promise((resolve, reject) => {
    if (!window?.DSHDEditorLib) {
      reject(new Error('Design Huddle editor library not found'))
    }

    window?.DSHDEditorLib?.getProjects(
      {
        limit: 100
      },
      (error, data) => {
        if (error) {
          console.error(error, data)
          reject(error)
        } else {
          resolve(data?.items || [])
        }
      }
    )
  })
}

/**
 * Get the {@link DHElementsData} for the current Design Huddle project.
 *
 * Promisifies the callback-based {@link DHEditorInstance.getProjectData} method.
 */
export const getProject = (editorInstance: DHEditorInstance): Promise<DHElementsData> => {
  return new Promise((resolve, reject) => {
    if (!editorInstance) {
      reject(new Error('Design Huddle editor instance not found'))
    }

    editorInstance.getProjectData({}, (error, data) => {
      if (error) {
        console.error(error, data)
        reject(error)
      } else if (!data) {
        reject(new Error('Editor instance unable to retrieve project data'))
      } else {
        resolve(data)
      }
    })
  })
}

export const getRefreshedAccessToken = (mode: DHUserMode): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      if (!window?.DSHDEditorLib) {
        throw new Error('Design Huddle editor library not found')
      }

      const refreshTokenMethod:
        | DHEditorLib['getGuestAccessToken']
        | DHEditorLib['getVisitorAccessToken'] = {
        [DHUserMode.GUEST]: window.DSHDEditorLib.getGuestAccessToken,
        [DHUserMode.VISITOR]: window.DSHDEditorLib?.getVisitorAccessToken
      }[mode]

      refreshTokenMethod(buildDHConfigObject({ mode }), (error, token) => {
        if (error) {
          console.error(error, token)
          reject(error)
        } else if (!token) {
          reject(new Error('Failed to get access token'))
        } else {
          resolve(token)
        }
      })
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * Create a new {@link DHProject} from a Design Huddle template.
 *
 * Promisifies the callback-based {@link DHEditorLib.createProject} method.
 */
export const createProject = (dhTemplateId: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!window?.DSHDEditorLib) {
      reject(new Error('Design Huddle editor library not found'))
    }

    window?.DSHDEditorLib?.createProject(
      {
        template_id: dhTemplateId
      },
      (error, data) => {
        if (error) {
          console.error(error, data)
          reject(error)
        } else if (!data?.project_id) {
          reject(new Error('Project creation failed to return a project ID'))
        } else {
          resolve(data.project_id)
        }
      }
    )
  })
}

/**
 * Injects the Design Huddle editor into the DOM as an iframe at the supplied `containerId` selector.
 *
 * Promisifies the callback-based {@link DHEditorLib.insert} method.
 */
export const insertEditor = (
  containerId: string,
  dhProjectId: string
): Promise<DHEditorInstance> => {
  return new Promise((resolve, reject) => {
    if (!window?.DSHDEditorLib) {
      reject(new Error('Design Huddle editor library not found'))
    }

    window?.DSHDEditorLib?.insert(
      containerId,
      {
        project_id: dhProjectId
      },
      (error, editor) => {
        if (error) {
          console.error(error, editor)
          reject(error)
        } else if (!editor) {
          reject(new Error('Inserting Design Huddle editor failed to return an editor instance'))
        } else {
          resolve(editor)
        }
      }
    )
  })
}

export const getProjectRender = (
  editorInstance: DHEditorInstance
): Promise<DHProjectGetRenderResult> => {
  return new Promise((resolve, reject) => {
    if (!editorInstance) {
      reject(new Error('Design Huddle editor instance not found'))
    }

    editorInstance.getRender({}, (error, data) => {
      if (error) {
        console.error(error, data)
        reject(error)
      } else if (!data) {
        reject(new Error('Editor instance unable to retrieve project render'))
      } else {
        resolve(data)
      }
    })
  })
}
